import { Article } from '../types';

export const companyProfileArticle: Article = {
  id: 'company-profile-setup',
  title: 'Creating Your Company Profile',
  slug: 'company-profile',
  description: 'Complete guide to setting up and managing your company profile with all business information, service areas, and operational details',
  category: 'getting-started',
  tags: ['company', 'profile', 'setup', 'business', 'information', 'settings'],
  lastUpdated: new Date('2024-03-02'),
  readTime: 12,
  content: `
    <h2>Setting Up Your Company Profile</h2>
    <p>Your company profile is the foundation of your CRM setup. It contains essential business information that appears on invoices, estimates, and customer communications. This guide will walk you through every aspect of creating and maintaining your company profile.</p>

    <div class="bg-blue-50 p-4 rounded-lg mt-4 mb-6">
      <strong>Why This Matters:</strong> A complete company profile ensures professional communication with customers, accurate documentation, and proper system configuration for your business operations.
    </div>

    <h2 id="accessing-profile">Accessing Company Profile Settings</h2>
    
    <p>To access your company profile settings:</p>
    <ol>
      <li>Log in to your CRM at <code>https://app.thedefinedcrm.com</code></li>
      <li>Click on <strong>Settings</strong> in the main navigation menu</li>
      <li>Select <strong>Company Profile</strong> from the settings menu</li>
      <li>You'll see multiple tabs for different profile sections</li>
    </ol>

    <div class="bg-yellow-50 p-4 rounded-lg mt-4 mb-6">
      <strong>Permission Note:</strong> Only users with Admin or Owner roles can edit company profile information. Other users can view but not modify these settings.
    </div>

    <h2 id="basic-information">Basic Company Information</h2>
    
    <h3>Legal Business Details</h3>
    <p>Start by entering your official business information:</p>
    <ul>
      <li><strong>Legal Business Name:</strong> Your registered business name (e.g., "Smith Cleaning Services LLC")</li>
      <li><strong>DBA (Doing Business As):</strong> Trade name if different from legal name (e.g., "Sparkle Clean")</li>
      <li><strong>Business Structure:</strong>
        <ul>
          <li>Sole Proprietorship</li>
          <li>Partnership</li>
          <li>Limited Liability Company (LLC)</li>
          <li>Corporation (C-Corp or S-Corp)</li>
        </ul>
      </li>
      <li><strong>Year Established:</strong> When your business was founded</li>
      <li><strong>Number of Employees:</strong> Current team size for proper licensing</li>
    </ul>

    <h3>Tax and Legal Information</h3>
    <p>Enter your business identification numbers:</p>
    <ul>
      <li><strong>Federal Tax ID (EIN):</strong> Your Employer Identification Number
        <ul>
          <li>Format: XX-XXXXXXX</li>
          <li>Required for tax reporting</li>
          <li>Keep this secure and private</li>
        </ul>
      </li>
      <li><strong>State Tax ID:</strong> State-specific tax identification</li>
      <li><strong>Business License Number:</strong> Local business permit number</li>
      <li><strong>Professional License Numbers:</strong> Any industry-specific certifications</li>
    </ul>

    <h3>Insurance Information</h3>
    <p>Document your business insurance for customer confidence:</p>
    <ul>
      <li><strong>General Liability Insurance:</strong>
        <ul>
          <li>Policy Number</li>
          <li>Coverage Amount (e.g., $1,000,000)</li>
          <li>Expiration Date</li>
          <li>Insurance Provider</li>
        </ul>
      </li>
      <li><strong>Workers' Compensation:</strong>
        <ul>
          <li>Policy Number</li>
          <li>State Filing Number</li>
          <li>Coverage Details</li>
        </ul>
      </li>
      <li><strong>Bonding Information:</strong> If your business is bonded</li>
      <li><strong>Additional Coverage:</strong> Vehicle, property, or specialty insurance</li>
    </ul>

    <h2 id="contact-information">Contact Information</h2>
    
    <h3>Primary Business Contacts</h3>
    <p>Set up your main communication channels:</p>
    <ol>
      <li><strong>Main Phone Number:</strong>
        <ul>
          <li>Primary business line</li>
          <li>Format: (XXX) XXX-XXXX</li>
          <li>This appears on all customer communications</li>
        </ul>
      </li>
      <li><strong>Alternative Phone:</strong> Secondary or after-hours number</li>
      <li><strong>Fax Number:</strong> If applicable for commercial clients</li>
      <li><strong>Emergency Contact:</strong> 24/7 number for urgent issues</li>
    </ol>

    <h3>Email Addresses</h3>
    <p>Configure email addresses for different purposes:</p>
    <ul>
      <li><strong>General Email:</strong> info@yourcompany.com
        <ul>
          <li>Main contact for general inquiries</li>
          <li>Displayed on website and marketing materials</li>
        </ul>
      </li>
      <li><strong>Support Email:</strong> support@yourcompany.com
        <ul>
          <li>Customer service inquiries</li>
          <li>Service-related questions</li>
        </ul>
      </li>
      <li><strong>Billing Email:</strong> billing@yourcompany.com
        <ul>
          <li>Invoice and payment questions</li>
          <li>Financial communications</li>
        </ul>
      </li>
      <li><strong>Sales Email:</strong> sales@yourcompany.com
        <ul>
          <li>New customer inquiries</li>
          <li>Quote requests</li>
        </ul>
      </li>
    </ul>

    <h3>Website and Social Media</h3>
    <p>Add your online presence information:</p>
    <ul>
      <li><strong>Company Website:</strong> https://www.yourcompany.com</li>
      <li><strong>Facebook Page:</strong> facebook.com/yourcompany</li>
      <li><strong>Instagram:</strong> @yourcompany</li>
      <li><strong>LinkedIn:</strong> linkedin.com/company/yourcompany</li>
      <li><strong>Twitter/X:</strong> @yourcompany</li>
      <li><strong>YouTube Channel:</strong> For video testimonials or tutorials</li>
      <li><strong>Google My Business:</strong> Link to your GMB profile</li>
    </ul>

    <h2 id="business-addresses">Business Addresses</h2>
    
    <h3>Physical Address</h3>
    <p>Your main business location:</p>
    <ol>
      <li><strong>Street Address:</strong> Full street address including suite/unit</li>
      <li><strong>City:</strong> City name</li>
      <li><strong>State/Province:</strong> State or province code</li>
      <li><strong>ZIP/Postal Code:</strong> Full postal code</li>
      <li><strong>Country:</strong> If operating internationally</li>
    </ol>

    <h3>Mailing Address</h3>
    <p>If different from physical address:</p>
    <ul>
      <li>PO Box information</li>
      <li>Separate billing address</li>
      <li>Corporate headquarters address</li>
    </ul>

    <h3>Service Addresses</h3>
    <p>For businesses with multiple locations:</p>
    <ul>
      <li>Branch office addresses</li>
      <li>Warehouse locations</li>
      <li>Equipment storage facilities</li>
    </ul>

    <h2 id="service-areas">Service Areas Configuration</h2>
    
    <h3>Defining Your Service Territory</h3>
    <p>Specify where you provide services:</p>
    <ol>
      <li><strong>Service Radius:</strong>
        <ul>
          <li>Miles from base location (e.g., "25 miles")</li>
          <li>Travel time limits (e.g., "45 minutes")</li>
        </ul>
      </li>
      <li><strong>ZIP Codes Served:</strong>
        <ul>
          <li>Add individual ZIP codes</li>
          <li>Import ZIP code lists</li>
          <li>Use ZIP code ranges</li>
        </ul>
      </li>
      <li><strong>Cities/Towns:</strong>
        <ul>
          <li>List all municipalities served</li>
          <li>Include suburbs and districts</li>
        </ul>
      </li>
      <li><strong>Counties:</strong>
        <ul>
          <li>Full counties served</li>
          <li>Partial county coverage notes</li>
        </ul>
      </li>
    </ol>

    <h3>Service Area Restrictions</h3>
    <p>Define any limitations:</p>
    <ul>
      <li><strong>Excluded Areas:</strong> Specific locations you don't service</li>
      <li><strong>Minimum Job Requirements:</strong> By distance or job value</li>
      <li><strong>Travel Fees:</strong> Additional charges for distant locations</li>
      <li><strong>Rush Service Areas:</strong> Zones with expedited service availability</li>
    </ul>

    <div class="bg-green-50 p-4 rounded-lg mt-4 mb-6">
      <strong>Pro Tip:</strong> Clearly defining service areas helps automatically filter leads and prevents booking appointments outside your coverage zone.
    </div>

    <h2 id="business-hours">Business Hours and Availability</h2>
    
    <h3>Standard Operating Hours</h3>
    <p>Set your regular business schedule:</p>
    <table>
      <thead>
        <tr>
          <th>Day</th>
          <th>Open</th>
          <th>Close</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Monday</td>
          <td>8:00 AM</td>
          <td>6:00 PM</td>
          <td>Open</td>
        </tr>
        <tr>
          <td>Tuesday</td>
          <td>8:00 AM</td>
          <td>6:00 PM</td>
          <td>Open</td>
        </tr>
        <tr>
          <td>Wednesday</td>
          <td>8:00 AM</td>
          <td>6:00 PM</td>
          <td>Open</td>
        </tr>
        <tr>
          <td>Thursday</td>
          <td>8:00 AM</td>
          <td>6:00 PM</td>
          <td>Open</td>
        </tr>
        <tr>
          <td>Friday</td>
          <td>8:00 AM</td>
          <td>6:00 PM</td>
          <td>Open</td>
        </tr>
        <tr>
          <td>Saturday</td>
          <td>9:00 AM</td>
          <td>3:00 PM</td>
          <td>Open</td>
        </tr>
        <tr>
          <td>Sunday</td>
          <td>Closed</td>
          <td>Closed</td>
          <td>Closed</td>
        </tr>
      </tbody>
    </table>

    <h3>Special Hours Settings</h3>
    <ul>
      <li><strong>Holiday Schedule:</strong>
        <ul>
          <li>Add specific holiday dates</li>
          <li>Set recurring holidays (e.g., Thanksgiving)</li>
          <li>Configure holiday surcharges</li>
        </ul>
      </li>
      <li><strong>Seasonal Hours:</strong>
        <ul>
          <li>Summer extended hours</li>
          <li>Winter reduced schedule</li>
          <li>Peak season adjustments</li>
        </ul>
      </li>
      <li><strong>Emergency Service Hours:</strong>
        <ul>
          <li>24/7 availability options</li>
          <li>After-hours rates</li>
          <li>On-call schedule</li>
        </ul>
      </li>
    </ul>

    <h3>Time Zone Configuration</h3>
    <ol>
      <li>Select your primary time zone</li>
      <li>Enable automatic daylight saving time adjustments</li>
      <li>Configure display preferences for customer communications</li>
    </ol>

    <h2 id="payment-tax">Payment and Tax Settings</h2>
    
    <h3>Accepted Payment Methods</h3>
    <p>Configure which payment types you accept:</p>
    <ul>
      <li>☑ <strong>Cash:</strong> In-person payments</li>
      <li>☑ <strong>Check:</strong> Personal or business checks</li>
      <li>☑ <strong>Credit Cards:</strong> Visa, MasterCard, Discover, Amex</li>
      <li>☑ <strong>Debit Cards:</strong> Direct debit transactions</li>
      <li>☑ <strong>ACH Transfer:</strong> Bank transfers for commercial accounts</li>
      <li>☑ <strong>Online Payments:</strong> Through customer portal</li>
      <li>☐ <strong>Financing:</strong> Third-party financing options</li>
      <li>☐ <strong>Cryptocurrency:</strong> Bitcoin, etc. (if applicable)</li>
    </ul>

    <h3>Payment Terms</h3>
    <p>Set default payment expectations:</p>
    <ul>
      <li><strong>Residential Customers:</strong>
        <ul>
          <li>Due on receipt</li>
          <li>Net 7 days</li>
          <li>Net 15 days</li>
        </ul>
      </li>
      <li><strong>Commercial Customers:</strong>
        <ul>
          <li>Net 30 days</li>
          <li>Net 45 days</li>
          <li>Net 60 days</li>
        </ul>
      </li>
      <li><strong>Late Payment Fees:</strong>
        <ul>
          <li>Percentage rate (e.g., 1.5% per month)</li>
          <li>Fixed fee (e.g., $25 late fee)</li>
          <li>Grace period (e.g., 5 days)</li>
        </ul>
      </li>
    </ul>

    <h3>Tax Configuration</h3>
    <p>Set up tax rates for your service areas:</p>
    <ol>
      <li><strong>Default Sales Tax Rate:</strong> Your primary tax rate (e.g., 8.25%)</li>
      <li><strong>Multiple Tax Rates:</strong>
        <ul>
          <li>Different rates by service area</li>
          <li>State tax variations</li>
          <li>Local municipality taxes</li>
        </ul>
      </li>
      <li><strong>Tax-Exempt Handling:</strong>
        <ul>
          <li>Non-profit exemptions</li>
          <li>Resale certificates</li>
          <li>Government entities</li>
        </ul>
      </li>
      <li><strong>Tax Display Options:</strong>
        <ul>
          <li>Include tax in prices</li>
          <li>Show tax separately</li>
          <li>Display tax breakdown on invoices</li>
        </ul>
      </li>
    </ol>

    <h2 id="invoice-settings">Invoice and Document Settings</h2>
    
    <h3>Invoice Configuration</h3>
    <p>Customize your invoice appearance and information:</p>
    <ul>
      <li><strong>Invoice Numbering:</strong>
        <ul>
          <li>Starting number (e.g., 1001)</li>
          <li>Prefix (e.g., "INV-")</li>
          <li>Format: INV-2024-0001</li>
        </ul>
      </li>
      <li><strong>Invoice Header:</strong>
        <ul>
          <li>Company name and logo placement</li>
          <li>Contact information display</li>
          <li>License numbers to include</li>
        </ul>
      </li>
      <li><strong>Invoice Footer:</strong>
        <ul>
          <li>Payment instructions</li>
          <li>Terms and conditions</li>
          <li>Thank you message</li>
          <li>Legal disclaimers</li>
        </ul>
      </li>
    </ul>

    <h3>Estimate Settings</h3>
    <p>Configure estimate documents:</p>
    <ul>
      <li><strong>Estimate Validity Period:</strong> How long quotes remain valid (e.g., 30 days)</li>
      <li><strong>Estimate Terms:</strong> Conditions and limitations</li>
      <li><strong>Approval Process:</strong> Electronic signature requirements</li>
      <li><strong>Conversion Settings:</strong> Auto-convert approved estimates to jobs</li>
    </ul>

    <h3>Email Templates</h3>
    <p>Set up default email communications:</p>
    <ul>
      <li>Invoice email template</li>
      <li>Estimate email template</li>
      <li>Payment receipt template</li>
      <li>Appointment confirmation template</li>
      <li>Service reminder template</li>
    </ul>

    <h2 id="certifications">Certifications and Credentials</h2>
    
    <h3>Professional Certifications</h3>
    <p>Add your business certifications:</p>
    <ul>
      <li><strong>Industry Certifications:</strong>
        <ul>
          <li>ISSA (International Sanitary Supply Association)</li>
          <li>BSCAI (Building Service Contractors Association)</li>
          <li>Green cleaning certifications</li>
          <li>Safety certifications</li>
        </ul>
      </li>
      <li><strong>Training Credentials:</strong>
        <ul>
          <li>OSHA compliance training</li>
          <li>Chemical handling certification</li>
          <li>Equipment operation licenses</li>
        </ul>
      </li>
      <li><strong>Business Accreditations:</strong>
        <ul>
          <li>BBB Accreditation</li>
          <li>Chamber of Commerce membership</li>
          <li>Industry association memberships</li>
        </ul>
      </li>
    </ul>

    <h3>Awards and Recognition</h3>
    <p>Showcase your achievements:</p>
    <ul>
      <li>Business awards received</li>
      <li>Customer service recognition</li>
      <li>Community involvement awards</li>
      <li>Industry rankings or ratings</li>
    </ul>

    <h2 id="company-policies">Company Policies</h2>
    
    <h3>Service Policies</h3>
    <p>Define your standard operating policies:</p>
    <ul>
      <li><strong>Cancellation Policy:</strong>
        <ul>
          <li>Notice required (e.g., 24 hours)</li>
          <li>Cancellation fees</li>
          <li>Rescheduling options</li>
        </ul>
      </li>
      <li><strong>Satisfaction Guarantee:</strong>
        <ul>
          <li>Service guarantee terms</li>
          <li>Re-cleaning policy</li>
          <li>Refund conditions</li>
        </ul>
      </li>
      <li><strong>Property Damage Policy:</strong>
        <ul>
          <li>Insurance coverage details</li>
          <li>Claim procedures</li>
          <li>Liability limitations</li>
        </ul>
      </li>
      <li><strong>Privacy Policy:</strong>
        <ul>
          <li>Customer data handling</li>
          <li>Information security measures</li>
          <li>Third-party sharing policies</li>
        </ul>
      </li>
    </ul>

    <h3>Employee Policies</h3>
    <p>Document your team standards:</p>
    <ul>
      <li>Background check requirements</li>
      <li>Drug testing policy</li>
      <li>Uniform and appearance standards</li>
      <li>Code of conduct</li>
      <li>Training requirements</li>
    </ul>

    <h2 id="saving-changes">Saving and Updating Your Profile</h2>
    
    <h3>Save Options</h3>
    <p>After making changes to your company profile:</p>
    <ol>
      <li><strong>Save Draft:</strong> Save changes without publishing</li>
      <li><strong>Save and Publish:</strong> Make changes immediately active</li>
      <li><strong>Preview Changes:</strong> See how changes will appear</li>
      <li><strong>Cancel:</strong> Discard all unsaved changes</li>
    </ol>

    <h3>Change History</h3>
    <p>The system maintains a history of profile changes:</p>
    <ul>
      <li>View previous versions</li>
      <li>See who made changes and when</li>
      <li>Restore previous settings if needed</li>
      <li>Export change logs for compliance</li>
    </ul>

    <h2 id="best-practices">Best Practices</h2>
    
    <h3>Profile Completeness</h3>
    <ul>
      <li>✓ Complete all required fields</li>
      <li>✓ Keep information current and accurate</li>
      <li>✓ Update insurance expiration dates promptly</li>
      <li>✓ Review and update quarterly</li>
      <li>✓ Verify all contact information regularly</li>
    </ul>

    <h3>Professional Presentation</h3>
    <ul>
      <li>Use professional email addresses (avoid personal emails)</li>
      <li>Maintain consistent business hours</li>
      <li>Keep certifications and licenses current</li>
      <li>Provide complete address information</li>
      <li>Include all relevant business credentials</li>
    </ul>

    <h3>Customer Trust Building</h3>
    <ul>
      <li>Display insurance information prominently</li>
      <li>Include professional certifications</li>
      <li>Maintain accurate service area information</li>
      <li>Keep payment and tax information transparent</li>
      <li>Clearly state all policies and guarantees</li>
    </ul>

    <h2 id="troubleshooting">Common Issues and Solutions</h2>
    
    <h3>Profile Not Saving</h3>
    <ul>
      <li><strong>Check required fields:</strong> Ensure all mandatory fields are completed</li>
      <li><strong>Verify permissions:</strong> Confirm you have admin access</li>
      <li><strong>Browser issues:</strong> Clear cache or try different browser</li>
      <li><strong>Session timeout:</strong> Log in again if session expired</li>
    </ul>

    <h3>Information Not Displaying</h3>
    <ul>
      <li><strong>On invoices:</strong> Check invoice template settings</li>
      <li><strong>On website:</strong> Verify integration settings</li>
      <li><strong>In emails:</strong> Review email template configuration</li>
    </ul>

    <h3>Tax Calculation Issues</h3>
    <ul>
      <li>Verify tax rates are entered correctly</li>
      <li>Check service area tax assignments</li>
      <li>Ensure tax-exempt settings are configured</li>
      <li>Review rounding preferences</li>
    </ul>

    <div class="bg-blue-50 p-4 rounded-lg mt-6">
      <strong>Need Help?</strong> If you encounter issues setting up your company profile, contact our support team at support@definedcleaning.com or call (123) 456-7890. We're here to help ensure your profile is set up correctly.
    </div>
  `,
  tableOfContents: [
    { id: 'accessing-profile', title: 'Accessing Company Profile Settings' },
    { id: 'basic-information', title: 'Basic Company Information' },
    { id: 'contact-information', title: 'Contact Information' },
    { id: 'business-addresses', title: 'Business Addresses' },
    { id: 'service-areas', title: 'Service Areas Configuration' },
    { id: 'business-hours', title: 'Business Hours and Availability' },
    { id: 'payment-tax', title: 'Payment and Tax Settings' },
    { id: 'invoice-settings', title: 'Invoice and Document Settings' },
    { id: 'certifications', title: 'Certifications and Credentials' },
    { id: 'company-policies', title: 'Company Policies' },
    { id: 'saving-changes', title: 'Saving and Updating Your Profile' },
    { id: 'best-practices', title: 'Best Practices' },
    { id: 'troubleshooting', title: 'Common Issues and Solutions' }
  ],
  relatedArticles: [
    'first-login-setup',
    'invoice-settings',
    'service-configuration',
    'tax-configuration',
    'business-hours-setup'
  ]
};